import React from 'react';

/**
 * Componente para mostrar el escudo de Chile estilizado
 */
const EscudoChile = ({ className = '' }) => (
  <div className={`inline-flex items-center justify-center ${className}`}>
    <svg 
      width="20" 
      height="24" 
      viewBox="0 0 20 24" 
      className="fill-current"
    >
      {/* Escudo de Chile simplificado */}
      <path 
        d="M10 2L3 4v8c0 5 7 10 7 10s7-5 7-10V4l-7-2z" 
        className="fill-gray-800 stroke-gray-900"
        strokeWidth="0.5"
      />
      {/* Estrella central */}
      <path 
        d="M10 6l1.5 3h3l-2.5 2 1 3-3-2-3 2 1-3-2.5-2h3z" 
        className="fill-white"
      />
    </svg>
  </div>
);

/**
 * Componente para mostrar una patente de vehículo con diseño chileno auténtico
 * Basado en el formato oficial de patentes chilenas reales
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.patente - Número de patente a mostrar
 * @param {string} props.className - Clases adicionales
 * @param {string} props.size - Tamaño del componente ('xs', 'sm', 'md', 'lg')
 * @returns {JSX.Element} - Componente de visualización de patente
 */
const PatenteDisplay = ({ patente, className = '', size = 'md' }) => {
  // Formatear la patente para mostrarla correctamente
  const formatearPatente = (valor) => {
    if (!valor) return { letras: '', numeros: '' };
    
    // Eliminar espacios y guiones y convertir a mayúsculas
    const valorLimpio = String(valor).replace(/[\s-]/g, '').toUpperCase();
    
    // Formato nuevo: LL99LL (ej. GK SB 78 -> GK•SB, 78)
    if (/^[A-Z]{2}\d{2}[A-Z]{2}$/.test(valorLimpio)) {
      const letras1 = valorLimpio.substring(0, 2);
      const numeros = valorLimpio.substring(2, 4);
      const letras2 = valorLimpio.substring(4);
      return {
        letras: `${letras1} • ${letras2}`,
        numeros: numeros
      };
    }
    
    // Formato antiguo: LLLL99 (ej. ABCD12 -> AB•CD, 12)
    if (/^[A-Z]{4}\d{2}$/.test(valorLimpio)) {
      const letras1 = valorLimpio.substring(0, 2);
      const letras2 = valorLimpio.substring(2, 4);
      const numeros = valorLimpio.substring(4);
      return {
        letras: `${letras1} • ${letras2}`,
        numeros: numeros
      };
    }
    
    // Formato para motocicletas: LL999 (ej. AB123)
    if (/^[A-Z]{2}\d{3}$/.test(valorLimpio)) {
      return {
        letras: valorLimpio.substring(0, 2),
        numeros: valorLimpio.substring(2)
      };
    }
    
    // Si no coincide con ningún formato conocido, dividir letras y números
    const letras = valorLimpio.replace(/\d/g, '');
    const numeros = valorLimpio.replace(/[A-Z]/g, '');
    return {
      letras: letras,
      numeros: numeros
    };
  };
  
  const { letras, numeros } = formatearPatente(patente);
  
  // Configuración de tamaños
  const sizeConfig = {
    xxs: {
      container: 'px-1 py-1',
      text: 'text-xs',
      escudo: { width: '6', height: '8' },
      chile: 'text-[6px]',
      tracking: 'tracking-[0.02em]'
    },
    xs: {
      container: 'px-1 py-0.5',
      text: 'text-sm md:text-base',
      escudo: { width: '8', height: '10' },
      chile: 'text-[8px]',
      tracking: 'tracking-[0.05em]'
    },
    sm: {
      container: 'px-1.5 py-0.5',
      text: 'text-lg md:text-xl',
      escudo: { width: '10', height: '12' },
      chile: 'text-[10px]',
      tracking: 'tracking-[0.1em]'
    },
    md: {
      container: 'px-2 py-1',
      text: 'text-3xl md:text-4xl',
      escudo: { width: '14', height: '16' },
      chile: 'text-xs',
      tracking: 'tracking-[0.15em]'
    },
    lg: {
      container: 'px-3 py-1.5',
      text: 'text-4xl md:text-5xl',
      escudo: { width: '18', height: '20' },
      chile: 'text-sm',
      tracking: 'tracking-[0.2em]'
    }
  };
  
  const config = sizeConfig[size] || sizeConfig.md;
  
  return (
    <div className={`inline-flex items-center ${className}`}>
      <div className="relative bg-gray-200 rounded-md border-2 border-black overflow-hidden shadow-md">
        {/* Contenedor principal de la patente */}
        <div className={`flex flex-col ${config.container}`}>
          {/* Número de patente principal con escudo */}
          <div className="flex items-center justify-center space-x-1">
            {/* Letras */}
            <span className={`${config.text} font-semibold tracking-normal text-gray-700 font-sans`}>
              {letras}
            </span>
            
            {/* Escudo de Chile como separador */}
            <div className="mx-0.5">
              <svg 
                width={config.escudo.width}
                height={config.escudo.height}
                viewBox="0 0 20 24" 
                className="fill-current"
              >
                <path 
                  d="M10 2L3 4v8c0 5 7 10 7 10s7-5 7-10V4l-7-2z" 
                  className="fill-gray-800"
                />
                <path 
                  d="M10 6l1.5 3h3l-2.5 2 1 3-3-2-3 2 1-3-2.5-2h3z" 
                  className="fill-white"
                />
              </svg>
            </div>
            
            {/* Números */}
            <span className={`${config.text} font-semibold tracking-normal text-gray-700 font-sans`}>
              {numeros}
            </span>
          </div>
          
          {/* Texto "CHILE" con espaciado - Solo para tamaños mayores a xxs */}
          {size !== 'xxs' && (
            <div className={`text-center ${config.chile} font-bold text-black ${config.tracking} -mt-1`}>
              C H I L E
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatenteDisplay;
